import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../utils/apiConfig';
import '../styles-super-admin-dashboard.css';

const formatDuration = (secondsRaw) => {
  const seconds = Math.floor(secondsRaw || 0);
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);

  const parts = [];
  if (d) parts.push(`${d}d`);
  if (h || d) parts.push(`${h}h`);
  parts.push(`${m}m`);
  return parts.join(' ');
};

const SuperAdminMonitoring = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchStats = async () => {
    try {
      setError('');
      const url = `${API_BASE_URL}/admin/server-stats`;
      const response = await axios.get(url, {
        // No auth header – this endpoint is internal and sits behind SuperAdmin UI
        timeout: 5000
      });
      setStats(response.data || null);
    } catch (err) {
      console.error('Error loading server stats', err);
      setError('Unable to reach backend monitoring endpoint.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rooms = Array.isArray(stats?.rooms) ? stats.rooms : [];

  return (
    <div className="dashboard-overview">
      <section className="overview-hero surface-card">
        <div className="overview-hero-text">
          <span className="overview-eyebrow">Live Observability</span>
          <h2>PlayLive server monitoring</h2>
          <p>
            Super Admin view of Socket.IO connections, auction rooms and server uptime. Use this panel during
            tournament day to keep an eye on traffic.
          </p>
        </div>
        <div className="overview-hero-meta">
          <div className="overview-hero-date">
            <span>Backend status</span>
            <strong>{loading ? 'Loading…' : error ? 'Degraded' : 'Online'}</strong>
          </div>
        </div>
      </section>

      <section className="operations-grid">
        <div className="surface-card system-health-panel">
          <div className="panel-header minimal">
            <div>
              <p className="panel-eyebrow">Sockets</p>
              <h3>Real-time load</h3>
            </div>
            <span className="panel-meta">Auto-refreshes every 5s</span>
          </div>
          {loading && (
            <div className="loading-state-modern">
              <div className="loading-spinner-modern" />
              <p>Loading live metrics…</p>
            </div>
          )}
          {!loading && error && (
            <div className="attention-list">
              <p className="empty-attention-state">{error}</p>
              <p className="empty-attention-state">Check `pm2 status` and Nginx routing to the backend.</p>
            </div>
          )}
          {!loading && !error && (
            <div className="system-health-grid">
              <div className="health-stat">
                <p>Connected users</p>
                <strong>{stats?.sockets ?? 0}</strong>
                <span>Active Socket.IO clients</span>
              </div>
              <div className="health-stat">
                <p>Rooms</p>
                <strong>{rooms.length}</strong>
                <span>Active Socket.IO rooms</span>
              </div>
              <div className="health-stat">
                <p>Uptime</p>
                <strong>{formatDuration(stats?.uptime)}</strong>
                <span>Node.js process uptime</span>
              </div>
            </div>
          )}
        </div>

        <div className="surface-card attention-panel">
          <div className="panel-header minimal">
            <div>
              <p className="panel-eyebrow">Socket.IO Admin UI</p>
              <h3>Deep inspection</h3>
            </div>
            <span className="panel-meta">For engineering use</span>
          </div>
          <div className="attention-list">
            <article className="attention-item">
              <div>
                <strong>Open admin console</strong>
                <span>Live events, rooms and payloads</span>
              </div>
              <div className="attention-meta">
                <span className="attention-pill info">Step 1</span>
                <span>Visit&nbsp;<code>https://admin.socket.io</code></span>
              </div>
            </article>
            <article className="attention-item">
              <div>
                <strong>Connect to PlayLive</strong>
                <span>Point admin UI at production backend</span>
              </div>
              <div className="attention-meta">
                <span className="attention-pill info">Step 2</span>
                <span>Server URL:&nbsp;<code>https://app.playlivepro.com</code></span>
              </div>
            </article>
            <article className="attention-item">
              <div>
                <strong>Monitor auctions</strong>
                <span>Track rooms like <code>tournament:CODE</code> and bid events</span>
              </div>
              <div className="attention-meta">
                <span className="attention-pill muted">Tip</span>
                <span>Filter by events: <code>join-auction</code>, <code>new-bid</code>, <code>auction-started</code></span>
              </div>
            </article>
          </div>
        </div>

        <div className="surface-card system-health-panel">
          <div className="panel-header minimal">
            <div>
              <p className="panel-eyebrow">PM2</p>
              <h3>Process health</h3>
            </div>
            <span className="panel-meta">Run on server</span>
          </div>
          <div className="attention-list">
            <article className="attention-item">
              <div>
                <strong>Quick status</strong>
                <span>Check process list and restarts</span>
              </div>
              <div className="attention-meta">
                <span className="attention-pill info">SSH</span>
                <span><code>pm2 status</code></span>
              </div>
            </article>
            <article className="attention-item">
              <div>
                <strong>Live monitor</strong>
                <span>CPU / RAM per process</span>
              </div>
              <div className="attention-meta">
                <span className="attention-pill info">SSH</span>
                <span><code>pm2 monit</code></span>
              </div>
            </article>
            <article className="attention-item">
              <div>
                <strong>Restart API</strong>
                <span>Use during deployments or incidents</span>
              </div>
              <div className="attention-meta">
                <span className="attention-pill warning">Caution</span>
                <span><code>pm2 restart playlive-api</code></span>
              </div>
            </article>
          </div>
        </div>
      </section>

      {!loading && !error && rooms.length > 0 && (
        <section className="dashboard-cards-grid">
          <section className="dashboard-card surface-card">
            <div className="dashboard-card-header">
              <h3 className="dashboard-card-title">ACTIVE ROOMS</h3>
              <div className="dashboard-card-subtitles">
                <span className="dashboard-card-subtitle-1">Socket.IO topology</span>
                <span className="dashboard-card-subtitle-2">{rooms.length} rooms</span>
              </div>
            </div>
            <div className="dashboard-card-content">
              <ul className="sports-list">
                {rooms.map((room) => (
                  <li key={room} className="sport-item">
                    <span className="sport-name">{room}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </section>
      )}
    </div>
  );
};

export default SuperAdminMonitoring;

